import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { noop, uniq } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import { setPurchaseOrderInfo } from 'state/inboundTicket'
import { setPurchaseOrderFilter } from 'state/purchaseOrder'
import FormModalHeading from 'components/generic/modals/form-modal/FormModalHeading'
import { Divider, Grid, Stack } from '@mui/material_modern'
import { useGetPurchaseOrdersV2Query } from 'services/purchaseOrders'
import { dateFormatter } from 'utils/data-manipulation/valueFormatter'
import { calculateTotalWeight } from 'utils/data-manipulation/common'
import { getColumns } from 'components/generic/datagrid/utils'
import Chip from 'components/generic/chip/Chip'
import { purchaseOrderFieldsNonSortable } from 'components/containers/contracts/purchase-orders/purchaseOrdersHelpers'
import { setCustomer } from 'state/customer'
import { DECIMAL_PRECISION_PRESETS, MEASURE_UNIT, PAGE_SIZE } from 'utils/constants'
import { convertUM, setDecimalPrecision } from 'utils/helpers'
import DataGridWithSearchPaginated from 'components/generic/datagrid/DataGridWithSearchPaginated'

const LinkPurchaseOrder = ({ onFormStateChange, onHideModal }) => {
  const dispatch = useDispatch()
  const { disableStatusFilter } = useSelector(state => state.ui.formModalProps)
  const { purchaseOrderNumber } = useSelector(state => state.inboundTicket.transportationInformation)
  const { filters } = useSelector(state => state.purchaseOrder)
  const { customer } = useSelector(state => state.customer)
  const { defaultYardUm } = useSelector(state => state.settings)
  const [activeFilters, setActiveFilters] = useState(getActiveFiltersArray(filters, disableStatusFilter))
  const initialApiFilter = transformFiltersToApiFilter(activeFilters, customer?.id)

  const [apiParameters, setApiParameters] = useState({
    page: 1,
    pageSize: PAGE_SIZE,
    filter: initialApiFilter,
  })

  const { data, isFetching } = useGetPurchaseOrdersV2Query({
    ...apiParameters,
    filter: JSON.stringify(apiParameters.filter),
  })

  const updateApiParameters = updates => {
    setApiParameters(prev => ({
      ...prev,
      ...updates,
    }))
  }
  const handlePageChange = pageNumber => {
    updateApiParameters({ page: pageNumber + 1 }) // mui is 0 indexed, api is 1 indexed
  }
  /**
   * handle free text search changes
   */
  const handleSearchChange = searchInput => {
    updateApiParameters({ filter: { ...apiParameters.filter, search: searchInput } })
  }

  /**
   * update supplierId and statuses in apiParameters.filter when activeFilters change
   */
  useEffect(() => {
    const newApiFilter = transformFiltersToApiFilter(activeFilters, customer?.id)
    // we leave filter.search alone, but update the rest of the filter object
    const updatedApiFilterArg = {
      ...newApiFilter,
      search: apiParameters.filter.search,
    }
    updateApiParameters({ filter: updatedApiFilterArg })
  }, [activeFilters])

  /**
   * update activeFilters when filters change
   */
  useEffect(() => {
    const newActiveFilters = getActiveFiltersArray(filters, disableStatusFilter)
    setActiveFilters(newActiveFilters)
  }, [filters, disableStatusFilter])

  const handleLink = supplierInfo => {
    const { purchaseOrderId, purchaseOrderNumber: purchaseOrderNum, supplier } = supplierInfo
    const { id, displayName } = supplier
    let value = ''
    if (purchaseOrderNum !== purchaseOrderNumber) {
      value = purchaseOrderNum
    }
    dispatch(
      setPurchaseOrderInfo({
        purchaseOrderNumber: value || '',
        purchaseOrderId: value ? purchaseOrderId || '' : '',
      }),
    )
    dispatch(setCustomer({ id, customerCommonIdentifierString: displayName }))
    dispatch(
      setPurchaseOrderFilter({
        filter: 'customer',
        value: displayName,
      }),
    )
    onHideModal()
  }

  const transformRows = () => {
    if (data?.Items) {
      return data.Items.slice().map(purchaseOrder => {
        const { priceEntries } = purchaseOrder
        const hasMultipleWeightUnits = uniq(priceEntries.map(m => m.weightUnits?.toUpperCase())).length > 1
        const um = (priceEntries?.[0]?.weightUnits || defaultYardUm).toUpperCase()
        const totalWeight = calculateTotalWeight(priceEntries)
        const convertedFulfilled = convertUM(purchaseOrder.fulfilledAmount, defaultYardUm, um)

        return {
          id: purchaseOrder.id,
          readableId: purchaseOrder.purchaseOrderTicketId,
          purchaseOrderNumber: purchaseOrder.purchaseOrderTicketId,
          customerOrderNumber: purchaseOrder.customerSONumber,
          supplier: purchaseOrder.supplier,
          effectiveDate: dateFormatter(purchaseOrder.effectiveDate),
          customerName: purchaseOrder.supplier?.displayName,
          materials: priceEntries,
          ordered: hasMultipleWeightUnits
            ? 'Mixed'
            : `${setDecimalPrecision(totalWeight, DECIMAL_PRECISION_PRESETS.WEIGHT)} ${um}`,
          remain: hasMultipleWeightUnits
            ? 'Mixed'
            : `${setDecimalPrecision(totalWeight - convertedFulfilled, DECIMAL_PRECISION_PRESETS.WEIGHT)} ${um}`,
          isLinked: {
            linkedId: purchaseOrderNumber || '',
            onLink: handleLink,
          },
        }
      })
    }
    return [] // fallback
  }

  const columns = [
    'isLinked',
    'purchaseOrderNumber',
    'customerOrderNumber',
    'effectiveDate',
    'customerName',
    'materials',
    'ordered',
    'remain',
  ]

  useEffect(() => {
    onFormStateChange(false)
  }, [])

  useEffect(() => {
    // This sets the customer filter when linking a PO from an IBT
    if (customer?.customerCommonIdentifierString) {
      dispatch(
        setPurchaseOrderFilter({
          filter: 'customer',
          value: customer.customerCommonIdentifierString,
        }),
      )
    } else {
      // Reset or remove the customer filter when customerCommonIdentifierString is empty
      dispatch(
        setPurchaseOrderFilter({
          filter: 'customer',
          value: '',
        }),
      )
    }
  }, [customer])

  return (
    <Stack>
      <FormModalHeading title="Link Purchase Order" backButtonPosition="right" onBack={() => onHideModal()} />
      <Divider />
      <Grid p={3} sx={{ height: '100%' }} container flexDirection="column">
        <DataGridWithSearchPaginated
          rowCount={data?.totalResultsCount || 0}
          rows={transformRows()}
          fetchRows={handlePageChange}
          loading={isFetching}
          columns={getColumns(columns, purchaseOrderFieldsNonSortable)}
          textSearchPlaceholder="PO# or Customer"
          handleSearchChange={handleSearchChange}
          showFilterChips
          filterChips={
            <>
              {activeFilters.map((filterInput, index) => (
                <Chip
                  key={filterInput?.name}
                  label={`${filterInput?.name}: ${filterInput?.value}`}
                  onDelete={
                    filterInput?.name === 'Status'
                      ? undefined
                      : () => {
                          const newActiveFilters = activeFilters.slice()
                          newActiveFilters.splice(index, 1)
                          setActiveFilters(newActiveFilters)
                        }
                  }
                />
              ))}
            </>
          }
        />
      </Grid>
    </Stack>
  )
}

LinkPurchaseOrder.propTypes = {
  onFormStateChange: PropTypes.func,
  onHideModal: PropTypes.func,
}

LinkPurchaseOrder.defaultProps = {
  onFormStateChange: noop,
  onHideModal: noop,
}

export default LinkPurchaseOrder

/**
 * takes filters object and returns an array of active filters
 * @param filters
 * @param disableStatusFilter
 */
function getActiveFiltersArray(filters, disableStatusFilter) {
  return Object.keys(filters)
    .filter(key => {
      if (disableStatusFilter && key === 'status') {
        return false // omit status filter if disabled
      }
      return !!filters[key].value
    })
    .map(filterItem => filters[filterItem])
}

/**
 * takes activeFilters array and selectedSupplierId and returns an object that can be passed to the api
 * @param activeFilters
 * @param selectedSupplierId
 * @return {{}}
 */
function transformFiltersToApiFilter(activeFilters, selectedSupplierId) {
  const apiFilterObj = {}
  activeFilters.forEach(f => {
    if (f?.name === 'Customer' && f?.value) {
      // don't use filter.value, Instead if filter is set we can pass the customer.id representing the selected customer (a.k.a. supplier)
      apiFilterObj.supplierId = selectedSupplierId
    }
    if (f?.name === 'Status' && f.value) {
      apiFilterObj.statuses = [f?.value]
    }
  })
  return apiFilterObj
}
